import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import api from "../../axiosInstance";
import toast from "react-hot-toast";

// ── existing thunks ──
export const submitReview = createAsyncThunk(
  "review/submit",
  async (formData, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/review/reviews`, formData, {
        timeout: 60000, // uploads on mobile data are slow; override any short default
      });
      return data.review;
    } catch (err) {
      let message;

      if (err.code === "ECONNABORTED") {
        message = "Upload timed out. Check your connection and try again.";
      } else if (!err.response) {
        message = "Network error. Check your connection and try again.";
      } else if (err.response.status === 413) {
        message = "Photos are too large. Try smaller photos.";
      } else {
        message =
          err.response.data?.message ||
          `Failed to submit review (${err.response.status})`;
      }

      return rejectWithValue(message);
    }
  },
);

export const checkReviewStatus = createAsyncThunk(
  "review/checkStatus",
  async ({ orderId, productId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/review/reviews/check`, {
        params: { orderId, productId },
      });
      return { productId, ...data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  },
);

// ── fetch public reviews for a product ──
export const fetchProductReviews = createAsyncThunk(
  "review/fetchProductReviews",
  async (productId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/review/product/${productId}`);
      return data.reviews; // array from your backend
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to load reviews",
      );
    }
  },
);

export const fetchRatingStats = createAsyncThunk(
  "review/fetchRatingStats",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/review/stats`);
      return data.stats; // { averageRating, totalReviews, distribution }
    } catch (err) {
      return rejectWithValue(err.response?.data?.message);
    }
  },
);

const reviewSlice = createSlice({
  name: "review",
  initialState: {
    reviewStatus: {}, // { [productId]: { hasReviewed, review } }
    reviews: [], // ← NEW: current product's reviews
    reviewsLoading: false,
    reviewsError: null,
    submitting: false,
    error: null,
    stats: { averageRating: 0, totalReviews: 0 },
    statsLoading: false,
  },
  reducers: {
    clearReviewError: (state) => {
      state.error = null;
    },
    clearProductReviews: (state) => {
      state.reviews = [];
      state.reviewsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // submitReview
      .addCase(submitReview.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(submitReview.fulfilled, (state, action) => {
        // ...existing logic
        // simplest: bump locally
        const { totalReviews, averageRating } = state.stats;
        state.stats.averageRating =
          (averageRating * totalReviews + action.payload.rating) /
          (totalReviews + 1);
        state.stats.totalReviews = totalReviews + 1;
      })
      .addCase(submitReview.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
        toast.error(action.payload);
      })

      // checkReviewStatus
      .addCase(checkReviewStatus.fulfilled, (state, action) => {
        const { productId, hasReviewed, review } = action.payload;
        state.reviewStatus[productId] = { hasReviewed, review };
      })

      // fetchProductReviews
      .addCase(fetchProductReviews.pending, (state) => {
        state.reviewsLoading = true;
        state.reviewsError = null;
      })
      .addCase(fetchProductReviews.fulfilled, (state, action) => {
        state.reviewsLoading = false;
        state.reviews = action.payload;
        state.averageRating = action.payload.length
          ? action.payload.reduce((sum, r) => sum + r.rating, 0) /
            action.payload.length
          : 0;
      })
      .addCase(fetchProductReviews.rejected, (state, action) => {
        state.reviewsLoading = false;
        state.reviewsError = action.payload;
      })
      // extraReducers — add:
      .addCase(fetchRatingStats.pending, (state) => {
        state.statsLoading = true;
      })
      .addCase(fetchRatingStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload;
      })
      .addCase(fetchRatingStats.rejected, (state) => {
        state.statsLoading = false;
      });
  },
});

export const { clearReviewError, clearProductReviews } = reviewSlice.actions;
// ── selectors ──
export const selectProductReviews = (state) => state.review.reviews;

export const selectReviewCount = (state) => state.review.reviews.length;

export const selectAverageRating = (state) => {
  const reviews = state.review.reviews;
  if (!reviews.length) return 0;

  const total = reviews.reduce((sum, review) => sum + review.rating, 0);
  return total / reviews.length;
};
export default reviewSlice.reducer;
